//pages/careers.js
import Careers from "../components/main-pages/Careers";
import Head from "next/head";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import HreflangLinks from "../components/common/HrefLinkTags";
import API from "../components/http/config.js";
import { withPageSuffix } from "../utils/seo";

const SITE = "https://www.prism-me.com";

const BRAND = { en: "Prism Digital", ar: "بريزم ديجيتال" };

// Last-resort fallbacks, used only when the key is missing in the CURRENT language
const FALLBACKS = {
  en: {
    metaTitle: "Careers | Prism Digital",
    metaDescription:
      "Explore open positions at Prism Digital and join our digital marketing team.",
    pageSuffix: (p) => `Page ${p}`,
    pageTitleBase: "Careers",
  },
  ar: {
    metaTitle: "الوظائف | بريزم ديجيتال",
    metaDescription:
      "اطّلع على الوظائف الشاغرة في بريزم ديجيتال وانضم إلى فريقنا في التسويق الرقمي.",
    pageSuffix: (p) => `الصفحة ${p}`,
    pageTitleBase: "الوظائف",
  },
};

const parsePage = (value) => {
  const n = parseInt(value || "1", 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

export default function CareersPage({
  initialCareers = [],
  initialTotalPages = 1,
  enExists = true,
  arExists = true,
}) {
  const { t, i18n } = useTranslation("careers");
  const router = useRouter();
  const { locale, query } = router;
  const lang = locale === "ar" ? "ar" : "en";
  const page = parsePage(query.page);

  const fb = FALLBACKS[lang];
  const brand = BRAND[lang];

  // Use the translation only if it exists in the current language,
  // otherwise our own fallback (skips i18next's fallbackLng to en)
  const tr = (key, vars, fallback) =>
    i18n.getResource(lang, "careers", key) ? t(key, vars) : fallback;

  const base =
    locale === "ar" ? `${SITE}/${locale}/careers` : `${SITE}/careers`;

  // Page 1 => clean URL, page 2+ => self-canonical
  const canonical = page > 1 ? `${base}?page=${page}` : base;

  // Title + description: i18n keys -> language fallbacks
  let title = tr("careers.seo.metaTitle", {}, fb.metaTitle);
  let description = tr("careers.seo.metaDescription", {}, fb.metaDescription);

  if (page > 1) {
    const suffix = tr("careers.seo.pageSuffix", { page }, fb.pageSuffix(page));
    const titleBase = tr("careers.seo.pageTitleBase", {}, fb.pageTitleBase);
    title = `${titleBase} – ${suffix} | ${brand}`;
    description = withPageSuffix(description, suffix);
  }

  return (
    <>
      {/* The page itself is always listed. The other language is listed
          only if that exact address (same ?page=) loads. */}
      <HreflangLinks
        enPath={enExists ? "/careers" : null}
        arPath={arExists ? "/ar/careers" : null}
        params={{
          ...(page > 1 && { page }),
        }}
      />
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Head>
      <Careers
        initialCareers={initialCareers}
        initialTotalPages={initialTotalPages}
      />
    </>
  );
}

export async function getServerSideProps({ locale, query, res }) {
  const lang = locale || "en";
  const otherLang = lang === "ar" ? "en" : "ar";
  const page = parsePage(query.page);
  const limit = 9;
  const i18nNamespaces = ["header", "footer", "careers", "common"];

  if (query.page !== undefined && page === 1) {
    return {
      redirect: {
        destination: lang === "ar" ? "/ar/careers" : "/careers",
        statusCode: 301,
      },
    };
  }

  // Cache at CDN/browser so most hits skip SSR + the backend entirely
  res.setHeader(
    "Cache-Control",
    "public, s-maxage=300, stale-while-revalidate=900",
  );

  try {
    // Run everything at once instead of sequentially
    const [mainRes, otherRes, translations] = await Promise.all([
      API.get("/careers/all/careers", { params: { lang, page, limit } }),
      page > 1
        ? API.get("/careers/all/careers", {
            params: { lang: otherLang, page, limit },
          }).catch(() => null)
        : Promise.resolve(null),
      serverSideTranslations(lang, i18nNamespaces),
    ]);

    const data = mainRes.data;
    const slimCareers = (data?.careers ?? []).map((c) => ({
      _id: c._id,
      slug: c.slug,
      position: c.position,
      jobType: c.jobType,
      location: c.location,
      workLocationType: c.workLocationType,
    }));

    if (page > 1 && slimCareers.length === 0) {
      const lastPage = Number(data?.totalPages) || 0;
      if (lang === "ar" && lastPage >= 1 && lastPage < page) {
        return {
          redirect: {
            destination:
              lastPage > 1 ? `/ar/careers?page=${lastPage}` : "/ar/careers",
            permanent: false,
          },
        };
      }
      return { notFound: true };
    }

    let otherHasPage = page === 1;
    if (page > 1 && otherRes) {
      const otherItems = otherRes.data?.careers ?? [];
      const otherTotal = Number(otherRes.data?.totalPages) || 0;
      otherHasPage =
        otherItems.length > 0 && (otherTotal === 0 || otherTotal >= page);
    }

    return {
      props: {
        ...translations,
        initialCareers: slimCareers,
        initialTotalPages: data?.totalPages ?? 1,
        enExists: lang === "en" ? true : otherHasPage,
        arExists: lang === "ar" ? true : otherHasPage,
      },
    };
  } catch (e) {
    console.error(
      "Careers getServerSideProps error:",
      e?.response?.status,
      e?.message,
    );
    res.setHeader("Cache-Control", "no-store"); // don't cache the error fallback
    return {
      props: {
        ...(await serverSideTranslations(lang, i18nNamespaces)),
        initialCareers: [],
        initialTotalPages: 1,
        enExists: lang === "en",
        arExists: lang === "ar",
      },
    };
  }
}
